import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export type Filtros = Record<string, string>

/**
 * Filtros e paginação na **URL**, não em `useState`.
 *
 * Três consequências que a operação usa todo dia:
 *
 *  1. o corretor manda para o gestor o link da lista já filtrada;
 *  2. o botão "voltar" do navegador desfaz o filtro, e não a tela inteira;
 *  3. o drill-down do dashboard é só uma navegação com query string — o mesmo
 *     conjunto de filtros que o servidor devolveu em `drilldown`
 *     (`05-dashboard-metrics.md §4`), sem a tela remontar a regra.
 *
 * `definir` com valor vazio **remove** a chave: mandar `equipeId=` faria a API
 * recusar com `422` em vez de ignorar o filtro.
 */
export function useFiltrosUrl(padroes: Filtros = {}) {
  const [parametros, setParametros] = useSearchParams()

  const filtros = useMemo<Filtros>(() => {
    const resultado: Filtros = { ...padroes }
    for (const [chave, valor] of parametros.entries()) {
      if (valor !== '') resultado[chave] = valor
      else delete resultado[chave]
    }
    return resultado
    // `padroes` é literal na chamada; comparar por conteúdo evitaria o laço.
    // Incluir o próprio objeto na lista o recriaria a cada render e o `useMemo`
    // nunca acertaria o cache — a comparação por conteúdo é intencional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametros, JSON.stringify(padroes)])

  const definir = useCallback(
    (novos: Record<string, string | number | boolean | null | undefined>) => {
      setParametros(
        (atuais) => {
          const proximos = new URLSearchParams(atuais)
          for (const [chave, valor] of Object.entries(novos)) {
            if (valor === null || valor === undefined || valor === '' || valor === false) {
              proximos.delete(chave)
            } else {
              proximos.set(chave, String(valor))
            }
          }
          // Qualquer mudança de filtro invalida o cursor da página atual: ele é
          // keyset sobre a consulta anterior e devolveria uma fatia sem relação
          // com o novo recorte.
          if (!('cursor' in novos)) proximos.delete('cursor')
          return proximos
        },
        { replace: true },
      )
    },
    [setParametros],
  )

  const limpar = useCallback(
    (manter: ReadonlyArray<string> = []) => {
      setParametros(
        (atuais) => {
          const proximos = new URLSearchParams()
          for (const chave of manter) {
            const valor = atuais.get(chave)
            if (valor) proximos.set(chave, valor)
          }
          return proximos
        },
        { replace: true },
      )
    },
    [setParametros],
  )

  /** Alterna ordenação: mesmo campo inverte a direção, campo novo começa desc. */
  const alternarOrdenacao = useCallback(
    (campo: string) => {
      const atual = parametros.get('ordenar') ?? ''
      const mesmoCampo = atual.replace(/^-/, '') === campo
      const descendente = atual.startsWith('-')
      definir({ ordenar: mesmoCampo && descendente ? campo : `-${campo}` })
    },
    [parametros, definir],
  )

  const ordenacao = useMemo(() => {
    const bruto = filtros.ordenar
    if (!bruto) return undefined
    return {
      campo: bruto.replace(/^-/, ''),
      direcao: bruto.startsWith('-') ? ('desc' as const) : ('asc' as const),
    }
  }, [filtros.ordenar])

  return { filtros, definir, limpar, alternarOrdenacao, ordenacao }
}
